import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { getOrder } from "../api";
import { useToast } from "../hooks/use-toast";

const OrderLookup = () => {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Order lookup · Phantom Industries";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const normalised = ref.trim().toUpperCase();
    if (!normalised) return;
    setLoading(true);
    try {
      await getOrder(normalised);
      navigate(`/order/${normalised}`);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? "No order found with that reference."
        : (err?.response?.data?.detail || "Lookup failed. Try again.");
      toast({ title: "Not found", description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24" data-testid="order-lookup-page">
      <section className="relative pb-10">
        <div className="absolute inset-0 aurora-purple opacity-70 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-14">
          <div className="chip">Order lookup</div>
          <h1 className="mt-4 font-headline text-5xl md:text-6xl font-bold leading-[0.95] text-gradient-purple">
            Track your build.<br />No account needed.
          </h1>
          <p className="mt-4 text-white/60 max-w-xl">
            Enter the reference we emailed you — something like{" "}
            <span className="font-mono text-purple-300">PP-XXXXXX</span> — to check
            payment status, view your invoice or resume a pending crypto payment.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={submit} className="card-glass rounded-3xl p-8" data-testid="order-lookup-form">
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                Order reference
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  required
                  placeholder="PP-XXXXXX"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="flex-1 min-w-[240px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 font-mono text-lg text-purple-300 tracking-widest outline-none focus:border-purple-500/50 focus:bg-purple-500/5 transition-colors"
                  data-testid="order-lookup-input"
                />
                <button
                  type="submit"
                  disabled={loading || !ref.trim()}
                  className="btn-primary"
                  data-testid="order-lookup-submit"
                >
                  {loading ? "Searching…" : (
                    <>Look up <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
              </div>
            </label>

            <div className="mt-6 pt-6 border-t border-white/5 grid md:grid-cols-2 gap-4 text-sm text-white/60">
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>
                  No login. No cookies. The reference alone is the key.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <KeyRound className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>
                  Lost the reference?{" "}
                  <a
                    href="/contact"
                    className="text-purple-300 hover:text-white underline underline-offset-4"
                  >
                    Contact us
                  </a>{" "}
                  from the same email you ordered with.
                </span>
              </div>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

export default OrderLookup;
